/**
 * User Manual — Premium SaaS Documentation UI
 * Sections match the actual user sidebar navigation
 * Uses inline styles for layout to beat App.css specificity
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import {
  Lock, LayoutDashboard, Upload, FolderOpen, Users,
  Hash, Settings, BookOpen, Shield, KeyRound, Eye,
  Timer, Mail, FileCheck, Download, AlertTriangle,
  CheckCircle2, Info, Clock, Fingerprint, RefreshCw,
  HardDrive, Bell, FileUp, ShieldCheck,
  UserCheck, Files, FolderLock, Share2, Search,
} from 'lucide-react';

const SECTIONS = [
  { id: 'login',      num: '1.0', title: 'Login & OTP',        icon: Lock },
  { id: 'dashboard',  num: '2.0', title: 'Dashboard',           icon: LayoutDashboard },
  { id: 'upload',     num: '3.0', title: 'Upload & Encrypt',    icon: Upload },
  { id: 'myfiles',    num: '4.0', title: 'My Files',            icon: FolderLock },
  { id: 'allfiles',   num: '5.0', title: 'All Files',           icon: Files },
  { id: 'access',     num: '6.0', title: 'Access Requests',     icon: Share2 },
  { id: 'hash',       num: '7.0', title: 'Hash Calculator',     icon: Hash },
  { id: 'settings',   num: '8.0', title: 'Settings',            icon: Settings },
];

/* ── Inline style objects (beat App.css specificity) ─────────────── */

const S = {
  card: {
    background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 16,
    boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 4px 12px rgba(0,0,0,0.03)',
    overflow: 'hidden', transition: 'box-shadow 0.25s ease',
  },
  sidebarCard: {
    background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 16,
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)', overflow: 'hidden',
    position: 'sticky', top: 16,
  },
  alertInfo: {
    background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10, padding: '12px 14px',
    display: 'flex', gap: 10, alignItems: 'flex-start',
  },
  alertWarning: {
    background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, padding: '12px 14px',
    display: 'flex', gap: 10, alignItems: 'flex-start',
  },
  alertSuccess: {
    background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: '12px 14px',
    display: 'flex', gap: 10, alignItems: 'flex-start',
  },
  codeBlock: {
    background: '#0f172a', border: '1px solid #334155', borderRadius: 10, padding: 16,
    fontFamily: 'monospace', fontSize: 12, color: '#94a3b8', lineHeight: 1.7,
    overflowX: 'auto', margin: '12px 0',
  },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 12 },
  tableWrapper: {
    border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden', margin: '8px 0',
  },
  th: {
    textAlign: 'left', padding: '8px 12px', fontSize: 10, fontWeight: 700,
    textTransform: 'uppercase', letterSpacing: '0.05em',
    background: '#f8fafc', color: '#64748b', borderBottom: '1px solid #e2e8f0',
  },
  td: {
    padding: '8px 12px', color: '#475569', borderBottom: '1px solid #f1f5f9',
  },
  featureCard: {
    display: 'flex', alignItems: 'flex-start', gap: 10, padding: 12,
    background: '#f8fafc', border: '1px solid #f1f5f9', borderRadius: 10,
  },
  featureIcon: {
    flexShrink: 0, width: 28, height: 28, borderRadius: 8,
    background: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: '#3b82f6',
  },
  stepNum: {
    flexShrink: 0, width: 22, height: 22, borderRadius: '50%',
    background: '#dbeafe', color: '#1d4ed8', fontSize: 11, fontWeight: 700,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  sectionNum: {
    flexShrink: 0, width: 36, height: 36, borderRadius: '50%',
    background: '#2563eb', color: '#fff', fontSize: 14, fontWeight: 700,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    boxShadow: '0 2px 6px rgba(37,99,235,0.3)',
  },
  subHeading: {
    fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em',
    color: '#94a3b8', marginBottom: 10,
  },
  headerGradient: {
    background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 50%, #06b6d4 100%)',
    borderRadius: 16, padding: '24px 28px', marginBottom: 24, color: '#fff',
  },
};

/* ── Reusable components ─────────────────────────────────────────── */

function SectionCard({ children, id, visible }) {
  return (
    <section id={`um-${id}`} style={{
      ...S.card,
      opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(16px)',
      transition: 'opacity 0.5s ease, transform 0.5s ease, box-shadow 0.25s ease',
      scrollMarginTop: 16,
    }}>
      {children}
    </section>
  );
}

function SectionTitle({ num, title, icon: Icon }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, paddingBottom: 16, borderBottom: '1px solid #e2e8f0' }}>
      <span style={S.sectionNum}>{num.replace('.0', '')}</span>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }}>
        <Icon size={18} style={{ color: '#3b82f6', flexShrink: 0 }} />
        <h2 style={{ fontSize: 18, fontWeight: 700, color: '#0f172a', margin: 0 }}>{title}</h2>
      </div>
    </div>
  );
}

function AlertBox({ type = 'info', children }) {
  const cfg = { info: { s: S.alertInfo, c: '#1e40af', I: Info }, warning: { s: S.alertWarning, c: '#92400e', I: AlertTriangle }, success: { s: S.alertSuccess, c: '#166534', I: CheckCircle2 } }[type];
  return (
    <div style={cfg.s}>
      <cfg.I size={15} style={{ flexShrink: 0, marginTop: 2, color: cfg.c, opacity: 0.7 }} />
      <div style={{ fontSize: 12, lineHeight: 1.6, color: cfg.c }}>{children}</div>
    </div>
  );
}

function CodeBlock({ children }) {
  return <div style={S.codeBlock}>{children}</div>;
}

function FeatureGrid({ items }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 10 }}>
      {items.map((item) => (
        <div key={item.title} style={S.featureCard}>
          <span style={S.featureIcon}><item.icon size={14} /></span>
          <div>
            <p style={{ fontSize: 12, fontWeight: 600, color: '#1e293b', margin: 0 }}>{item.title}</p>
            <p style={{ fontSize: 11, color: '#64748b', margin: '3px 0 0' }}>{item.desc}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

function StepList({ steps }) {
  return (
    <ol style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
      {steps.map((step, i) => (
        <li key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
          <span style={S.stepNum}>{i + 1}</span>
          <p style={{ fontSize: 13, color: '#475569', margin: 0, lineHeight: 1.6 }}>{step}</p>
        </li>
      ))}
    </ol>
  );
}

function DataTable({ headers, rows }) {
  return (
    <div style={S.tableWrapper}>
      <table style={S.table}>
        <thead>
          <tr>{headers.map((h) => <th key={h} style={S.th}>{h}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#fafbfc' }}>
              {row.map((cell, j) => <td key={j} style={S.td}>{cell}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SubHeading({ children }) {
  return <p style={S.subHeading}>{children}</p>;
}

/* ── Main page ───────────────────────────────────────────────────── */

export default function UserManualPage() {
  const [activeId, setActiveId] = useState(SECTIONS[0].id);
  const [visible, setVisible] = useState({});
  const obs = useRef(null);
  const fadeObs = useRef(null);

  useEffect(() => {
    const nodes = SECTIONS.map((s) => document.getElementById(`um-${s.id}`)).filter(Boolean);
    if (!nodes.length) return;
    obs.current = new IntersectionObserver((entries) => {
      const vis = entries.filter((e) => e.isIntersecting);
      if (vis.length) {
        const top = vis.reduce((a, b) => (a.boundingClientRect.top < b.boundingClientRect.top ? a : b));
        setActiveId(top.target.id.replace('um-', ''));
      }
    }, { rootMargin: '-15% 0px -65% 0px', threshold: 0 });
    nodes.forEach((n) => obs.current.observe(n));
    return () => obs.current?.disconnect();
  }, []);

  useEffect(() => {
    const nodes = SECTIONS.map((s) => document.getElementById(`um-${s.id}`)).filter(Boolean);
    if (!nodes.length) return;
    fadeObs.current = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) setVisible((prev) => ({ ...prev, [e.target.id.replace('um-', '')]: true }));
      });
    }, { rootMargin: '0px 0px -10% 0px', threshold: 0.05 });
    nodes.forEach((n) => fadeObs.current.observe(n));
    return () => fadeObs.current?.disconnect();
  }, []);

  const scrollTo = useCallback((id) => {
    document.getElementById(`um-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto' }}>

      {/* Header */}
      <div style={S.headerGradient}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ width: 44, height: 44, borderRadius: 14, background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <BookOpen size={22} color="#fff" />
            </span>
            <div>
              <h1 style={{ fontSize: 20, fontWeight: 700, color: '#fff', margin: 0 }}>User Manual</h1>
              <p style={{ fontSize: 13, color: 'rgba(191,219,254,0.9)', margin: '4px 0 0' }}>Step-by-step guide for every feature in your CDEaaS workspace. Learn how to upload, encrypt, share, and manage your files securely.</p>
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 16 }}>
          {['8 Sections', 'Step-by-Step Guides', 'AES-256 Encryption', 'Last updated: Apr 2026'].map((t) => (
            <span key={t} style={{ fontSize: 10, fontWeight: 600, background: 'rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.9)', padding: '4px 10px', borderRadius: 20 }}>{t}</span>
          ))}
        </div>
      </div>

      {/* Grid: sidebar + content */}
      <div style={{ display: 'grid', gridTemplateColumns: '220px 1fr', gap: 24, alignItems: 'start' }}>

        {/* Sidebar */}
        <nav style={S.sidebarCard}>
          <div style={{ padding: '16px 16px 12px', borderBottom: '1px solid #f1f5f9' }}>
            <p style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#94a3b8', margin: 0 }}>Manual Sections</p>
          </div>
          <div style={{ padding: 8, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {SECTIONS.map((sec) => {
              const active = sec.id === activeId;
              const Icon = sec.icon;
              return (
                <button key={sec.id} type="button" onClick={() => scrollTo(sec.id)}
                  style={{
                    width: '100%', display: 'flex', alignItems: 'center', gap: 8,
                    padding: '8px 12px', borderRadius: 8, border: 'none', cursor: 'pointer', textAlign: 'left',
                    fontSize: 12, fontWeight: active ? 600 : 500,
                    background: active ? '#2563eb' : 'transparent',
                    color: active ? '#fff' : '#64748b',
                    transition: 'all 0.15s ease',
                  }}>
                  <Icon size={13} style={{ color: active ? 'rgba(191,219,254,0.9)' : '#94a3b8' }} />
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{sec.title}</span>
                </button>
              );
            })}
          </div>
        </nav>

        {/* Content */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20, minWidth: 0 }}>

          {/* ═══ 1.0 Login & OTP ═══ */}
          <SectionCard id="login" visible={visible.login !== false}>
            <div style={{ padding: '24px 28px' }}>
              <SectionTitle num="1.0" title="Login & OTP" icon={Lock} />
              <p style={{ fontSize: 13, color: '#64748b', marginBottom: 20, lineHeight: 1.7 }}>
                To access CDEaaS, you must sign in with your email and password, then verify your identity using a One-Time Password (OTP) sent to your email. This two-step process protects your account from unauthorized access.
              </p>
              <SubHeading>Key Features</SubHeading>
              <FeatureGrid items={[
                { icon: Mail, title: 'Email & Password', desc: 'Sign in using your registered email and password' },
                { icon: KeyRound, title: 'OTP Verification', desc: '6-digit code sent to your email for identity check' },
                { icon: Timer, title: 'OTP Expiry', desc: 'Code expires in 5 minutes — request new if needed' },
                { icon: Shield, title: 'Account Protection', desc: 'Lockout after too many failed attempts' },
              ]} />
              <div style={{ marginTop: 20 }}>
                <SubHeading>How to Login</SubHeading>
                <StepList steps={[
                  'Open the CDEaaS login page in your browser.',
                  'Enter your registered email address and password, then click Sign In.',
                  'A 6-digit OTP code is sent to your email inbox.',
                  'Open your email (check spam if not in inbox) and copy the OTP code.',
                  'Enter the OTP code on the verification screen and click Verify.',
                  'You are now logged in and redirected to your Dashboard.',
                ]} />
              </div>
              <div style={{ marginTop: 16 }}>
                <AlertBox type="warning">
                  OTP codes expire after <strong>5 minutes</strong>. If expired, click "Resend OTP" to get a new code. Make sure Caps Lock is off when typing your password.
                </AlertBox>
              </div>
            </div>
          </SectionCard>

          {/* ═══ 2.0 Dashboard ═══ */}
          <SectionCard id="dashboard" visible={visible.dashboard !== false}>
            <div style={{ padding: '24px 28px' }}>
              <SectionTitle num="2.0" title="Dashboard" icon={LayoutDashboard} />
              <p style={{ fontSize: 13, color: '#64748b', marginBottom: 20, lineHeight: 1.7 }}>
                The Dashboard is your home screen after login. It shows a quick summary of your files, recent activity, and important notifications — giving you a snapshot of your workspace at a glance.
              </p>
              <SubHeading>What You'll See</SubHeading>
              <FeatureGrid items={[
                { icon: FolderLock, title: 'File Summary', desc: 'Total encrypted files you own and their status' },
                { icon: Clock, title: 'Recent Activity', desc: 'Your latest uploads, downloads, and access events' },
                { icon: Bell, title: 'Notifications', desc: 'Pending access requests and system alerts' },
                { icon: Shield, title: 'Security Status', desc: 'Session info and last login timestamp' },
              ]} />
              <div style={{ marginTop: 20 }}>
                <SubHeading>How to Use</SubHeading>
                <StepList steps={[
                  'After login, you land on the Dashboard automatically.',
                  'Check the file summary cards to see your total encrypted files.',
                  'Review the Recent Activity feed for your latest actions.',
                  'Click on any notification to navigate directly to the relevant page.',
                  'Use the sidebar to navigate to other features.',
                ]} />
              </div>
              <div style={{ marginTop: 16 }}>
                <AlertBox type="info">
                  The Dashboard refreshes each time you visit. Your session will timeout after <strong>15 minutes</strong> of inactivity — a warning banner will appear before logout.
                </AlertBox>
              </div>
            </div>
          </SectionCard>

          {/* ═══ 3.0 Upload & Encrypt ═══ */}
          <SectionCard id="upload" visible={visible.upload !== false}>
            <div style={{ padding: '24px 28px' }}>
              <SectionTitle num="3.0" title="Upload & Encrypt" icon={Upload} />
              <p style={{ fontSize: 13, color: '#64748b', marginBottom: 20, lineHeight: 1.7 }}>
                This is the core feature of CDEaaS. You can upload any file and it will be <strong style={{ color: '#0f172a' }}>encrypted in your browser</strong> using AES-256-GCM before being sent to the server. The server never sees your original file or your encryption password.
              </p>
              <SubHeading>Key Features</SubHeading>
              <FeatureGrid items={[
                { icon: Shield, title: 'AES-256-GCM Encryption', desc: 'Military-grade encryption performed in your browser' },
                { icon: Eye, title: 'Zero-Knowledge', desc: 'Server never sees your original file or password' },
                { icon: KeyRound, title: 'Password-Based Key', desc: 'Your password is converted into an encryption key using PBKDF2' },
                { icon: FileUp, title: 'Any File Type', desc: 'Upload documents, images, PDFs, or any file type' },
              ]} />
              <div style={{ marginTop: 20 }}>
                <SubHeading>How to Upload & Encrypt</SubHeading>
                <StepList steps={[
                  'Click "Upload & Encrypt" in the sidebar.',
                  'Click the upload area or drag and drop your file into it.',
                  'Enter an encryption password — this will be used to lock the file.',
                  'Confirm your encryption password by typing it again.',
                  'Click "Encrypt & Upload" — the file is encrypted in your browser first.',
                  'Once complete, a success message appears and the file is saved to the server.',
                ]} />
              </div>
              <CodeBlock>
                Your Password → PBKDF2 (key derivation) → AES-256 Key{'\n'}
                Original File + AES Key + Random IV → Encrypted File{'\n'}
                Encrypted File → Uploaded to Server (only ciphertext stored)
              </CodeBlock>
              <AlertBox type="warning">
                <strong>IMPORTANT:</strong> Your encryption password is <strong>NEVER stored anywhere</strong>. If you forget it, the file cannot be recovered by anyone — not even the admin. Write it down or use a password manager.
              </AlertBox>
            </div>
          </SectionCard>

          {/* ═══ 4.0 My Files ═══ */}
          <SectionCard id="myfiles" visible={visible.myfiles !== false}>
            <div style={{ padding: '24px 28px' }}>
              <SectionTitle num="4.0" title="My Files" icon={FolderLock} />
              <p style={{ fontSize: 13, color: '#64748b', marginBottom: 20, lineHeight: 1.7 }}>
                View and manage all files you have uploaded. From here, you can decrypt and download your files, view file details, or delete files you no longer need.
              </p>
              <SubHeading>Key Features</SubHeading>
              <FeatureGrid items={[
                { icon: FolderOpen, title: 'File List', desc: 'See all your encrypted files with name, size, and date' },
                { icon: Download, title: 'Decrypt & Download', desc: 'Enter your password to decrypt and download any file' },
                { icon: Info, title: 'File Details', desc: 'View file metadata like upload date, size, and hash' },
                { icon: AlertTriangle, title: 'Delete Files', desc: 'Remove files you no longer need from the system' },
              ]} />
              <div style={{ marginTop: 20 }}>
                <SubHeading>How to Decrypt & Download</SubHeading>
                <StepList steps={[
                  'Click "My Files" in the sidebar to see your file list.',
                  'Find the file you want and click the Download / Decrypt button.',
                  'An OTP will be sent to your email for identity verification.',
                  'Enter the OTP code to verify it is really you.',
                  'Enter the encryption password you used when uploading the file.',
                  'The file is decrypted in your browser and downloaded to your device.',
                ]} />
              </div>
              <div style={{ marginTop: 16 }}>
                <AlertBox type="info">
                  Decryption happens <strong>entirely in your browser</strong>. The server only sends the encrypted blob — your password and the original file never touch the server.
                </AlertBox>
              </div>
            </div>
          </SectionCard>

          {/* ═══ 5.0 All Files ═══ */}
          <SectionCard id="allfiles" visible={visible.allfiles !== false}>
            <div style={{ padding: '24px 28px' }}>
              <SectionTitle num="5.0" title="All Files" icon={Files} />
              <p style={{ fontSize: 13, color: '#64748b', marginBottom: 20, lineHeight: 1.7 }}>
                Browse all files in the system uploaded by all users. You can see file names and owners, but you <strong style={{ color: '#0f172a' }}>cannot download or decrypt</strong> other users' files unless you request and receive access.
              </p>
              <SubHeading>Key Features</SubHeading>
              <FeatureGrid items={[
                { icon: Files, title: 'Browse All Files', desc: 'See every file in the system with owner info' },
                { icon: Search, title: 'Search & Filter', desc: 'Search by file name or owner to find specific files' },
                { icon: Share2, title: 'Request Access', desc: 'Send an access request to the file owner' },
                { icon: Shield, title: 'Privacy Protected', desc: 'You cannot open files without owner approval' },
              ]} />
              <div style={{ marginTop: 20 }}>
                <SubHeading>How to Request Access to a File</SubHeading>
                <StepList steps={[
                  'Click "All Files" in the sidebar.',
                  'Browse or search for the file you need.',
                  'Click the "Request Access" button next to the file.',
                  'A request is sent to the file owner for approval.',
                  'You will be notified when the owner approves or denies your request.',
                  'If approved, the file will appear in your accessible files.',
                ]} />
              </div>
              <div style={{ marginTop: 16 }}>
                <AlertBox type="info">
                  Even with approved access, you still need the <strong>encryption password</strong> from the file owner to decrypt the file. Access approval only grants download permission.
                </AlertBox>
              </div>
            </div>
          </SectionCard>

          {/* ═══ 6.0 Access Requests ═══ */}
          <SectionCard id="access" visible={visible.access !== false}>
            <div style={{ padding: '24px 28px' }}>
              <SectionTitle num="6.0" title="Access Requests" icon={Share2} />
              <p style={{ fontSize: 13, color: '#64748b', marginBottom: 20, lineHeight: 1.7 }}>
                Manage file sharing through a controlled request-and-approval workflow. As a file owner, you decide who can access your files. As a requester, you can track the status of your pending requests.
              </p>
              <SubHeading>Request Statuses</SubHeading>
              <DataTable headers={['Status', 'Meaning', 'What Happens Next']} rows={[
                ['Pending', 'Waiting for file owner to respond', 'Owner will approve or deny'],
                ['Approved', 'File owner granted you access', 'You can now download the file'],
                ['Denied', 'File owner rejected the request', 'You may request again later'],
              ]} />
              <div style={{ marginTop: 20 }}>
                <SubHeading>How to Manage Requests</SubHeading>
                <StepList steps={[
                  'Click "Access Requests" in the sidebar.',
                  'Switch between "Incoming" (others requesting your files) and "Outgoing" (your requests) tabs.',
                  'For incoming requests: review who is asking and which file they want.',
                  'Click "Approve" to grant access, or "Deny" to reject the request.',
                  'For outgoing requests: check the status column to see if your request was approved.',
                ]} />
              </div>
              <div style={{ marginTop: 16 }}>
                <AlertBox type="success">
                  You have <strong>full control</strong> over your files. Nobody can access your encrypted files without your explicit approval.
                </AlertBox>
              </div>
            </div>
          </SectionCard>

          {/* ═══ 7.0 Hash Calculator ═══ */}
          <SectionCard id="hash" visible={visible.hash !== false}>
            <div style={{ padding: '24px 28px' }}>
              <SectionTitle num="7.0" title="Hash Calculator" icon={Hash} />
              <p style={{ fontSize: 13, color: '#64748b', marginBottom: 20, lineHeight: 1.7 }}>
                The Hash Calculator lets you generate a SHA-256 hash of any file — directly in your browser without uploading it. Use this to verify file integrity: if the hash matches before and after encryption/decryption, the file is intact.
              </p>
              <SubHeading>Key Features</SubHeading>
              <FeatureGrid items={[
                { icon: Fingerprint, title: 'SHA-256 Hash', desc: 'Generate a unique fingerprint for any file' },
                { icon: Shield, title: 'Client-Side Only', desc: 'File is never uploaded — hash computed locally' },
                { icon: CheckCircle2, title: 'Integrity Check', desc: 'Compare hashes to verify file was not modified' },
                { icon: RefreshCw, title: 'Re-Hash Anytime', desc: 'Hash the file again after decryption to compare' },
              ]} />
              <div style={{ marginTop: 20 }}>
                <SubHeading>How to Verify File Integrity</SubHeading>
                <StepList steps={[
                  'Click "Hash Calculator" in the sidebar.',
                  'Select a file from your computer — the SHA-256 hash is generated instantly.',
                  'Copy and save the hash value (e.g., before uploading the file).',
                  'After decrypting the file later, hash it again using this same tool.',
                  'Compare both hash values — if they match exactly, the file is perfectly intact.',
                ]} />
              </div>
              <div style={{ marginTop: 16 }}>
                <AlertBox type="success">
                  This tool is completely independent. You can verify the hash using any external SHA-256 tool — the result will be identical.
                </AlertBox>
              </div>
            </div>
          </SectionCard>

          {/* ═══ 8.0 Settings ═══ */}
          <SectionCard id="settings" visible={visible.settings !== false}>
            <div style={{ padding: '24px 28px' }}>
              <SectionTitle num="8.0" title="Settings" icon={Settings} />
              <p style={{ fontSize: 13, color: '#64748b', marginBottom: 20, lineHeight: 1.7 }}>
                Manage your account preferences, update your display name, change your password, and customize your experience.
              </p>
              <SubHeading>Available Settings</SubHeading>
              <DataTable headers={['Setting', 'Description']} rows={[
                ['Display Name', 'Change how your name appears in the system'],
                ['Change Password', 'Update your account password (current password required)'],
                ['Dark Mode', 'Toggle between light and dark interface theme'],
                ['Session Info', 'View your current session details and last login'],
              ]} />
              <div style={{ marginTop: 20 }}>
                <SubHeading>How to Change Password</SubHeading>
                <StepList steps={[
                  'Click "Settings" in the sidebar.',
                  'Scroll to the "Change Password" section.',
                  'Enter your current password for verification.',
                  'Enter your new password and confirm it.',
                  'Click Save — a success message confirms the update.',
                ]} />
              </div>
              <div style={{ marginTop: 16 }}>
                <AlertBox type="info">
                  Your password is stored as a <strong>bcrypt hash</strong> — it is never visible to anyone, including administrators. The session timeout (15 minutes) is a system setting and cannot be changed.
                </AlertBox>
              </div>
            </div>
          </SectionCard>

          {/* Footer */}
          <div style={{ textAlign: 'center', fontSize: 11, color: '#94a3b8', padding: '16px 0', borderTop: '1px solid #f1f5f9' }}>
            JOSMY CDEaaS · User Manual · All encryption is performed client-side · v1.0
          </div>
        </div>
      </div>
    </div>
  );
}
